import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodB2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts/campaign_action_handler';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodMdlrPaymentStatus } from '@Common/types/modulr';
import { ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodJSONObject } from '@Common/types/zod/zod-json';
import { ZodNumberQuery } from '@Common/types/zod/zod-number-query';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const B2bCampaignsPayoutsStatusValue = ['pending', 'cancelled', 'completed'] as const;

export const ZodB2bCampaignsPayoutsStatus = z.enum(B2bCampaignsPayoutsStatusValue);
export type B2bCampaignsPayoutsStatus = z.infer<typeof ZodB2bCampaignsPayoutsStatus>;

export const ZodGetB2bCampaignsPayoutsOneResponse = z.object({
  id: ZodId,
  b2b_company_id: ZodId,
  b2b_campaign_id: ZodId,
  b2b_employee_id: ZodId,
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
  savings_funds_request_id: ZodId.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
  total_gross_amount: z.number().int(),
  user_currency: ZodCurrency,
  user_currency_amount: z.number().int(),
  tax_amount: z.number().int(),
  tax_info: ZodJSONObject.nullable(),
  status: ZodB2bCampaignsPayoutsStatus,
  payout_by: ZodB2bCampaignActionHandler.nullable(),
  transfer_id: z.string().nullable(),
  transfer_mdlr_status: ZodMdlrPaymentStatus.nullable(),
  transfer_amount: z.number().int().nullable(),
  transfer_created_at: z.date().nullable(),
  transfer_executed_at: z.date().nullable(),
});

export type GetB2bCampaignsPayoutsOneResponse = z.TypeOf<
  typeof ZodGetB2bCampaignsPayoutsOneResponse
>;

export const ZodGetB2bCampaignsPayoutsResponse = ZodGetB2bCampaignsPayoutsOneResponse.array();

export type GetB2bCampaignsPayoutsResponse = z.infer<typeof ZodGetB2bCampaignsPayoutsResponse>;

export const getB2bCampaignsPayoutsSortColumns: (keyof GetB2bCampaignsPayoutsOneResponse)[] = [
  'id',
  'created_at',
  'updated_at',
  'status',
  'b2b_employee_id',
  'firstname',
  'lastname',
  'savings_funds_request_id',
  'total_gross_amount',
  'user_currency_amount',
  'tax_amount',
  'transfer_id',
];

const ZodFilters = z
  .object({
    id: createZodArrayable(ZodNumberQuery),
    b2b_company_id: createZodArrayable(ZodNumberQuery),
    b2b_campaign_id: createZodArrayable(ZodNumberQuery),
    b2b_employee_id: createZodArrayable(ZodNumberQuery),
    savings_funds_request_id: createZodArrayable(ZodNumberQuery),
    status: createZodArrayable(ZodB2bCampaignsPayoutsStatus),
    firstname: createZodArrayable(ZodTrimmedString),
    lastname: createZodArrayable(ZodTrimmedString),
    transfer_id: createZodArrayable(ZodTrimmedString),
    transfer_mdlr_status: createZodArrayable(ZodMdlrPaymentStatus),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
  })
  .partial();

export const ZodGetB2bCampaignsPayoutsQuery = buildZodBoListQueryParsed(
  ZodFilters,
  getB2bCampaignsPayoutsSortColumns,
  { range: [0, 10000] },
);

export type GetB2bCampaignsPayoutsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getB2bCampaignsPayoutsSortColumns)[number]
>;
